'use client'

import NextImage from 'next/image'
import React from 'react'

import { cn } from '@/utilities/ui'
import { useImageSettings } from '@/components/Media/imageSettingsContext'
import { loaderDeCandidatos } from '@/components/Media/ImageMedia/payloadSizesLoader'

import type { ImagemCandidato } from './types'

// Placeholder base64 (idêntico ao usado em ImageMedia) para o modo «Blur».
const placeholderBlur =
  'data:image/png;base64,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'

export type HomeCardImageProps = {
  src: string
  /**
   * Tamanhos gerados pelo Payload para esta imagem (ver `payloadSizesLoader.ts`). Com ≥2 entradas o
   * card ganha `srcset` sem passar pelo `/_next/image`; ausente ⇒ comportamento idêntico ao de antes.
   */
  srcSizes?: ImagemCandidato[]
  alt: string
  /** Sempre `fill` nos cards da home (pai com `position: relative` + `overflow-hidden`). */
  className?: string
  sizes?: string
  priority?: boolean
  fetchPriority?: 'high' | 'low' | 'auto'
  draggable?: boolean
}

/**
 * Imagem dos cards da home (faixas editoriais) com paridade ao `ImageMedia`:
 * — modo «Blur» (padrão) ou «Cinza + fade-in» conforme a aba «Imagens» do Identidade do site;
 * — `onError` → cai na imagem fallback configurada (cobre destaque deletado / URL órfã 404).
 *
 * Renderiza um fragmento (fundo cinza opcional + `next/image`) DENTRO do pai relative já
 * existente do card — sem adicionar caixa de layout, preservando o DOM/medidas atuais.
 */
export function HomeCardImage({
  src,
  srcSizes,
  alt,
  className,
  sizes,
  priority,
  fetchPriority,
  draggable,
}: HomeCardImageProps) {
  const imageSettings = useImageSettings()
  const grayLoading = imageSettings.loadingBackground === 'gray'
  const useBlur = !grayLoading

  const [errored, setErrored] = React.useState(false)
  const [loaded, setLoaded] = React.useState(false)

  // onError → fallback configurado (uma vez; evita loop se o próprio fallback falhar).
  const effectiveSrc =
    errored && imageSettings.fallbackUrl && src !== imageSettings.fallbackUrl
      ? imageSettings.fallbackUrl
      : src

  // srcset a partir dos tamanhos do Payload (performance, 19/08/2026). Nunca sobre o fallback (URL
  // local, após erro) e só com pelo menos uma alternativa ao original.
  const loaderPayload = React.useMemo(
    () => (srcSizes && srcSizes.length >= 2 ? loaderDeCandidatos(srcSizes) : undefined),
    [srcSizes],
  )
  const usarLoaderPayload = loaderPayload != null && !errored && /^https?:\/\//i.test(effectiveSrc)

  return (
    <>
      {grayLoading && !loaded ? (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[var(--theme-elevation-100)]"
        />
      ) : null}
      <NextImage
        src={effectiveSrc}
        alt={alt}
        fill
        className={cn(
          className,
          grayLoading && 'transition-opacity duration-300 motion-reduce:transition-none',
          grayLoading && !loaded && 'opacity-0',
        )}
        sizes={sizes}
        priority={priority}
        fetchPriority={fetchPriority}
        placeholder={useBlur ? 'blur' : 'empty'}
        blurDataURL={useBlur ? placeholderBlur : undefined}
        decoding="async"
        draggable={draggable}
        onError={() => setErrored(true)}
        onLoad={() => setLoaded(true)}
        // Mídia R2 (URL absoluta) está atrás da Cloudflare, que dá 403 no otimizador do Next →
        // servir SEM otimizar (o browser carrega direto). Fallback local fica otimizado.
        loader={usarLoaderPayload ? loaderPayload : undefined}
        unoptimized={usarLoaderPayload ? false : /^https?:\/\//i.test(effectiveSrc)}
      />
    </>
  )
}
